import { FC, ReactNode, useMemo, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import useMeasure from 'react-use-measure'
import {
  Text,
  BorderRadius4,
  BorderRadius6,
  GreyGrey10,
  GreyGrey40,
  GreyGrey70,
  GreyGrey80,
  Primary13Regular,
  Primary13SemiBold,
  Totals,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import {
  calculateDueOnStartDate,
  calculateQuotePhaseSubTotals,
  calculateQuoteTotals,
  toMoney,
  Phase as CalculationPhase
} from '@sequencehq/utils'
import { adaptPhasesForCalculation } from './adaptPhasesForCalculation'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import {
  apiPresentationToQuotePresentation,
  getGroupByPhaseData,
  PhaseBlock,
  sortProductsByPresentationOrder
} from '@sequencehq/quote-content'
import { Currency } from '@sequencehq/api'
import { Card } from './NodeTimeline/Card'
import { CustomerLogoCircle } from './CustomerLogoCircle'
import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import { crossFadeMotionProps } from './previewCardMotion'

export type Quote = Omit<DashboardApi20240730.Quotes.Quote, 'presentation'> & {
  presentation: string | unknown
}
export type Customer = DashboardApi20240730.GetCustomer.GetCustomerResponse

export const QuotePreviewCard: FC<{
  quote: Quote
  customer: Customer
  action?: ReactNode
  startCollapsed?: boolean
}> = ({ quote, customer, action, startCollapsed = false }) => {
  const [collapsed, setCollapsed] = useState(startCollapsed)
  const [contentRef, contentBounds] = useMeasure()
  const currency = useMemo(() => quote.currency ?? 'USD', [quote.currency])

  const phases = useMemo(
    () => adaptPhasesForCalculation(quote.phases),
    [quote.phases]
  )

  const totalContractValue = useMemo(() => {
    return calculateQuoteTotals({
      startDate: quote.preliminaryBillingStartDate
        ? apiDatesAdapters.fromApi(quote.preliminaryBillingStartDate)
        : undefined,
      phases,
      currency
    }).totalDiscountedRate
  }, [phases, quote.preliminaryBillingStartDate, currency])

  const presentation = useMemo(() => {
    return typeof quote.presentation === 'string'
      ? quote.presentation
      : JSON.stringify(quote.presentation)
  }, [quote.presentation])

  return (
    <Card
      data-testid="automations.quotePreviewCard"
      border={`${GreyGrey40} 1px solid`}
      borderRadius={collapsed ? '8px' : '12px'}
      transition="border-radius 0.2s"
    >
      <motion.div
        initial={false}
        animate={{ height: contentBounds.height || 'auto' }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
      >
        <Box ref={contentRef} position="relative">
          <AnimatePresence initial={false} mode="popLayout">
            {collapsed ? (
              <motion.div key="collapsed" {...crossFadeMotionProps}>
                <CollapsedHeader
                  quoteTitle={quote.title}
                  customer={customer}
                  currency={currency}
                  totalContractValue={totalContractValue}
                  onClick={() => {
                    setCollapsed(false)
                  }}
                />
              </motion.div>
            ) : (
              <motion.div key="expanded" {...crossFadeMotionProps}>
                <Flex flexDir="column" gap="8px">
                  <Header
                    quoteTitle={quote.title}
                    customer={customer}
                    quoteNumber={quote.quoteNumber}
                    currency={currency}
                    totalContractValue={totalContractValue}
                    onClick={() => {
                      setCollapsed(true)
                    }}
                  />

                  <PhasesOverview
                    preliminaryBillingStartDate={
                      quote.preliminaryBillingStartDate
                    }
                    phases={phases}
                    presentation={presentation}
                    currency={currency}
                    totalContractValue={totalContractValue}
                  />

                  <Flex>{action}</Flex>
                </Flex>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </motion.div>
    </Card>
  )
}

const CollapsedHeader: FC<{
  customer: Customer
  quoteTitle: string | undefined
  totalContractValue: number
  currency: Currency
  onClick: () => void
}> = ({ customer, quoteTitle, totalContractValue, currency, onClick }) => {
  const title = useMemo(() => quoteTitle ?? 'Untitled quote', [quoteTitle])

  return (
    <Grid
      gridTemplateColumns="auto 1fr auto"
      width="100%"
      gridColumnGap="8px"
      alignItems="center"
      padding="3px 4px"
      onClick={onClick}
      cursor="pointer"
    >
      {customer && (
        <CustomerLogoCircle customer={customer} size={16} fallbackSize={16} />
      )}

      <Text {...Primary13SemiBold} color={GreyGrey70}>
        {title}
      </Text>

      {totalContractValue > 0 && (
        <Text {...Primary13Regular} color={GreyGrey70}>
          {toMoney({
            currency,
            value: String(totalContractValue)
          })}
        </Text>
      )}
    </Grid>
  )
}

const Header: FC<{
  customer: Customer
  quoteTitle: string | undefined
  totalContractValue: number
  currency: Currency
  quoteNumber: string
  onClick: () => void
}> = ({
  customer,
  quoteTitle,
  totalContractValue,
  currency,
  quoteNumber,
  onClick
}) => {
  const title = useMemo(() => quoteTitle ?? 'Untitled quote', [quoteTitle])

  return (
    <Flex
      alignItems="center"
      gap="12px"
      paddingX="4px"
      onClick={onClick}
      cursor="pointer"
    >
      {customer && (
        <CustomerLogoCircle customer={customer} size={32} fallbackSize={16} />
      )}

      <Flex justifyContent="space-between" flex="1">
        <Flex flexDir="column" gap="4px">
          <Text {...Primary13SemiBold} color={GreyGrey80}>
            {title}
          </Text>
          <Text {...Primary13Regular} color={GreyGrey70}>
            {quoteNumber}
          </Text>
        </Flex>

        {totalContractValue > 0 && (
          <Flex flexDir="column" gap="4px" textAlign="right">
            <Text {...Primary13SemiBold} color={GreyGrey80}>
              {toMoney({
                currency,
                value: String(totalContractValue)
              })}
            </Text>
            <Text {...Primary13Regular} color={GreyGrey70}>
              Total contract value
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

const PhasesOverview: FC<{
  preliminaryBillingStartDate: string | undefined
  phases: CalculationPhase[]
  presentation: string
  currency: Currency
  totalContractValue: number
}> = ({
  preliminaryBillingStartDate,
  phases,
  presentation: presentationProp,
  currency,
  totalContractValue
}) => {
  const dueOnStartDate = useMemo(() => {
    return calculateDueOnStartDate({
      startDate: preliminaryBillingStartDate
        ? apiDatesAdapters.fromApi(preliminaryBillingStartDate)
        : undefined,
      phases,
      currency
    })
  }, [phases, preliminaryBillingStartDate, currency])

  const presentation = useMemo(() => {
    return apiPresentationToQuotePresentation({
      presentation: JSON.parse(presentationProp)
    })
  }, [presentationProp])

  const groupByPhaseData = useMemo(() => {
    const phaseBlock = presentation.blocks.find(
      block => block.blockType === 'phases'
    ) as PhaseBlock | undefined

    const subTotals = calculateQuotePhaseSubTotals({
      startDate: preliminaryBillingStartDate
        ? apiDatesAdapters.fromApi(preliminaryBillingStartDate)
        : undefined,
      phases,
      currency
    })

    return getGroupByPhaseData({
      phases: phaseBlock?.config.phases ?? [],
      subTotals
    })
  }, [presentation, preliminaryBillingStartDate, phases, currency])

  return (
    <Flex
      paddingX="8px"
      paddingY="4px"
      backgroundColor={GreyGrey10}
      borderRadius={BorderRadius6}
    >
      <Totals currency={currency}>
        {groupByPhaseData.map((phase, index) => {
          const orderedPrices = sortProductsByPresentationOrder({
            products: phase.prices,
            orderedProductIds:
              presentation.settings.priceEditorProductOrder ?? []
          })

          const orderedMinimums = sortProductsByPresentationOrder({
            products: phase.minimum ?? [],
            orderedProductIds:
              presentation.settings.priceEditorProductOrder ?? []
          })

          return (
            <>
              {typeof phase.subTotal === 'undefined' || phase.subTotal === 0 ? (
                <Totals.Description
                  key={phase.phaseId}
                  title={phase.label}
                  emphasised
                />
              ) : (
                <Totals.Row
                  key={phase.phaseId}
                  title={phase.label}
                  amount={phase.subTotal}
                  emphasisedTitle
                />
              )}

              {orderedPrices.map(price => (
                <Totals.Description key={price.priceId} title={price.name} />
              ))}

              {orderedMinimums.length > 0 && (
                <>
                  <Totals.Description title="Minimum commitment" />

                  {orderedMinimums.map(price => (
                    <Flex key={price.priceId} marginLeft="8px">
                      <Totals.Description title={price.name} />
                    </Flex>
                  ))}
                </>
              )}

              {index !== groupByPhaseData.length - 1 && <Totals.Spacer />}
            </>
          )
        })}

        {totalContractValue > 0 && (
          <>
            <Totals.Divider strong={dueOnStartDate === 0} />
            <Totals.Row
              title="Overall total"
              amount={totalContractValue}
              emphasised
            />
          </>
        )}

        {dueOnStartDate > 0 && (
          <>
            <Totals.Divider strong />
            <Totals.Row
              title="Due on start date"
              amount={dueOnStartDate}
              emphasised
            />
          </>
        )}
      </Totals>
    </Flex>
  )
}
